import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, BookOpen, Moon, Sun, Star, ArrowRight, ArrowLeft, Plus, Minus, Lightbulb, X, Heart } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { useLanguage } from '../i18n/LanguageContext';
import { ASCENDANT_INTERPRETATIONS, PLANET_INTERPRETATIONS, ASPECT_INTERPRETATIONS, HOUSES_INTERPRETATIONS, PlanetInterpretation } from '../data/natalInterpretations';
import { ENCYCLOPEDIA_ARTICLES, EncyclopediaArticle } from '../data/astrologyEncyclopedia';
interface InterpretationsHubPageProps {
  onNavigate: (page: string) => void;
}
type Article = PlanetInterpretation & {
  id: string;
  category: string;
  isEncyclopedia?: boolean;
  summary?: string;
  sections?: {
    heading: string;
    text: string;
  }[];
  keyFacts?: string[];
  commonMyths?: string[];
  tip?: string;
  relatedService?: string;
  relatedServiceLabel?: string;
};
// Flatten all data into a single array for easy searching and filtering
const getAllArticles = (): Article[] => {
  const articles: Article[] = [];
  // Add Ascendant interpretations
  Object.entries(ASCENDANT_INTERPRETATIONS).forEach(([idx, data]) => {
    articles.push({
      ...data,
      id: `asc-${idx}`,
      category: 'ascendant'
    });
  });
  // Add Planet interpretations
  Object.entries(PLANET_INTERPRETATIONS).forEach(([planet, signs]) => {
    Object.entries(signs).forEach(([idx, data]) => {
      articles.push({
        ...data,
        id: `planet-${planet}-${idx}`,
        category: 'planets-signs'
      });
    });
  });
  // Add Aspect interpretations
  Object.entries(ASPECT_INTERPRETATIONS).forEach(([key, data]) => {
    articles.push({
      ...data,
      id: `aspect-${key}`,
      category: 'aspects'
    });
  });
  // Add Houses interpretations
  Object.entries(HOUSES_INTERPRETATIONS).forEach(([key, data]) => {
    articles.push({
      ...data,
      id: `house-${key}`,
      category: 'planets-houses'
    });
  });
  // Add Encyclopedia articles
  ENCYCLOPEDIA_ARTICLES.forEach(article => {
    articles.push({
      id: article.id,
      title: article.title,
      category: article.category,
      text: article.summary,
      strengths: [],
      challenges: [],
      isEncyclopedia: true,
      summary: article.summary,
      sections: article.sections,
      keyFacts: article.keyFacts,
      commonMyths: article.commonMyths,
      tip: article.tip,
      relatedService: article.relatedService,
      relatedServiceLabel: article.relatedServiceLabel
    });
  });
  // Sort alphabetically by title
  return articles.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
};
const ALL_ARTICLES = getAllArticles();
const RUSSIAN_ALPHABET = ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Э', 'Ю', 'Я'];
const ENGLISH_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
export function InterpretationsHubPage({
  onNavigate
}: InterpretationsHubPageProps) {
  const {
    t,
    language
  } = useLanguage();
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const alphabet = language === 'ru' ? RUSSIAN_ALPHABET : ENGLISH_ALPHABET;
  const selectedArticle = useMemo(() => {
    return ALL_ARTICLES.find(a => a.id === selectedArticleId) || null;
  }, [selectedArticleId]);
  const filteredArticles = useMemo(() => {
    return ALL_ARTICLES.filter(article => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return article.title.toLowerCase().includes(query) || article.text.toLowerCase().includes(query);
      }
      // Letter filter
      if (selectedLetter) {
        return article.title.toUpperCase().startsWith(selectedLetter);
      }
      // Category filter
      if (selectedCategory) {
        return article.category === selectedCategory;
      }
      return false; // If no filter is active, we don't show the full list in the grid, we show categories
    });
  }, [searchQuery, selectedLetter, selectedCategory]);
  const isFiltering = searchQuery !== '' || selectedLetter !== null || selectedCategory !== null;
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedLetter(null);
    setSelectedCategory(null);
  };
  const handleLetterClick = (letter: string) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null);
    } else {
      setSelectedLetter(letter);
      setSearchQuery('');
      setSelectedCategory(null);
    }
  };
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSearchQuery('');
    setSelectedLetter(null);
  };
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedLetter(null);
    setSelectedCategory(null);
  };
  // ---------------------------------------------------------------------------
  // DETAIL VIEW
  // ---------------------------------------------------------------------------
  if (selectedArticle) {
    return <div className="w-full bg-slate-50 min-h-screen pb-16 sm:pb-24" data-id="element-20783">
        <div className="bg-white border-b border-slate-200 pt-6 pb-8 sm:pt-8 sm:pb-12" data-id="element-20784">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20785">
            <Breadcrumbs items={[{
            label: t('nav.knowledge'),
            onClick: () => onNavigate('home')
          }, {
            label: t('enc.badge'),
            onClick: () => setSelectedArticleId(null)
          }, {
            label: selectedArticle.title
          }]} className="mb-6 sm:mb-8" data-id="element-20786" />

            <button onClick={() => setSelectedArticleId(null)} className="flex items-center text-slate-500 hover:text-primary transition-colors mb-4 sm:mb-6 text-sm font-medium" data-id="element-20787">
              <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-20788" />
              {t('enc.backToList')}
            </button>

            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6" data-id="element-20789">
              {selectedArticle.title}
            </h1>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12" data-id="element-20790">
          <div className="prose prose-slate prose-lg max-w-none" data-id="element-20791">
            {selectedArticle.isEncyclopedia ? <>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-8 sm:mb-10 font-medium" data-id="element-20792">
                  {selectedArticle.summary}
                </p>

                {selectedArticle.sections?.map((section, idx) => <div key={idx} className="mb-8 sm:mb-10" data-id="element-20793">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-20794">
                      {section.heading}
                    </h2>
                    <p className="text-slate-700 leading-relaxed text-base sm:text-lg" data-id="element-20795">
                      {section.text}
                    </p>
                  </div>)}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 my-8 sm:my-10 not-prose" data-id="element-20796">
                  <Card padding="lg" className="border-indigo-200 bg-indigo-50/50" data-id="element-20797">
                    <div className="flex items-center mb-4 text-indigo-700" data-id="element-20798">
                      <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2" data-id="element-20799" />
                      <h3 className="text-base sm:text-lg font-bold" data-id="element-20800">
                        {t('enc.keyFacts')}
                      </h3>
                    </div>
                    <ul className="space-y-2.5 sm:space-y-3 text-indigo-900 text-sm" data-id="element-20801">
                      {selectedArticle.keyFacts?.map((fact, i) => <li key={i} className="flex items-start" data-id="element-20802">
                          <span className="mr-2 mt-0.5 text-indigo-400" data-id="element-20803">•</span>
                          <span data-id="element-20804">{fact}</span>
                        </li>)}
                    </ul>
                  </Card>

                  <Card padding="lg" className="border-rose-200 bg-rose-50/50" data-id="element-20805">
                    <div className="flex items-center mb-4 text-rose-700" data-id="element-20806">
                      <X className="w-5 h-5 sm:w-6 sm:h-6 mr-2" data-id="element-20807" />
                      <h3 className="text-base sm:text-lg font-bold" data-id="element-20808">
                        {t('enc.commonMyths')}
                      </h3>
                    </div>
                    <ul className="space-y-2.5 sm:space-y-3 text-rose-900 text-sm" data-id="element-20809">
                      {selectedArticle.commonMyths?.map((myth, i) => <li key={i} className="flex items-start" data-id="element-20810">
                          <span className="mr-2 mt-0.5 text-rose-400" data-id="element-20811">•</span>
                          <span data-id="element-20812">{myth}</span>
                        </li>)}
                    </ul>
                  </Card>
                </div>

                {selectedArticle.tip && <div className="bg-amber-50 border-l-4 border-amber-500 p-4 sm:p-6 my-6 sm:my-8 not-prose rounded-r-xl" data-id="element-20813">
                    <div className="flex items-start" data-id="element-20814">
                      <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 mr-2.5 sm:mr-3 flex-shrink-0 mt-0.5" data-id="element-20815" />
                      <div data-id="element-20816">
                        <h4 className="font-bold text-amber-900 mb-1.5 sm:mb-2 text-sm sm:text-base" data-id="element-20817">
                          {t('enc.astrologerTip')}
                        </h4>
                        <p className="text-amber-800 text-xs sm:text-sm leading-relaxed" data-id="element-20818">
                          {selectedArticle.tip}
                        </p>
                      </div>
                    </div>
                  </div>}
              </> : <>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-8 sm:mb-10" data-id="element-20819">
                  {selectedArticle.text}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 my-8 sm:my-10 not-prose" data-id="element-20820">
                  <Card padding="lg" className="border-emerald-200 bg-emerald-50/50" data-id="element-20821">
                    <div className="flex items-center mb-4 text-emerald-700" data-id="element-20822">
                      <Plus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" data-id="element-20823" />
                      <h3 className="text-base sm:text-lg font-bold" data-id="element-20824">
                        {t('enc.strengths')}
                      </h3>
                    </div>
                    <ul className="space-y-2.5 sm:space-y-3 text-emerald-900 text-sm" data-id="element-20825">
                      {selectedArticle.strengths.map((str, i) => <li key={i} className="flex items-start" data-id="element-20826">
                          <span className="mr-2 mt-0.5 text-emerald-400" data-id="element-20827">
                            •
                          </span>
                          <span data-id="element-20828">{str}</span>
                        </li>)}
                    </ul>
                  </Card>

                  <Card padding="lg" className="border-rose-200 bg-rose-50/50" data-id="element-20829">
                    <div className="flex items-center mb-4 text-rose-700" data-id="element-20830">
                      <Minus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" data-id="element-20831" />
                      <h3 className="text-base sm:text-lg font-bold" data-id="element-20832">
                        {t('enc.challenges')}
                      </h3>
                    </div>
                    <ul className="space-y-2.5 sm:space-y-3 text-rose-900 text-sm" data-id="element-20833">
                      {selectedArticle.challenges.map((chal, i) => <li key={i} className="flex items-start" data-id="element-20834">
                          <span className="mr-2 mt-0.5 text-rose-400" data-id="element-20835">•</span>
                          <span data-id="element-20836">{chal}</span>
                        </li>)}
                    </ul>
                  </Card>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 sm:p-6 my-6 sm:my-8 not-prose rounded-r-xl" data-id="element-20837">
                  <div className="flex items-start" data-id="element-20838">
                    <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 mr-2.5 sm:mr-3 flex-shrink-0 mt-0.5" data-id="element-20839" />
                    <div data-id="element-20840">
                      <h4 className="font-bold text-amber-900 mb-1.5 sm:mb-2 text-sm sm:text-base" data-id="element-20841">
                        {t('enc.astrologerTip')}
                      </h4>
                      <p className="text-amber-800 text-xs sm:text-sm leading-relaxed" data-id="element-20842">
                        {t('enc.defaultTip')}
                      </p>
                    </div>
                  </div>
                </div>
              </>}
          </div>

          {/* Related Articles */}
          <div className="mt-12 sm:mt-16" data-id="element-20843">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-4 sm:mb-6" data-id="element-20844">
              {t('enc.relatedArticles')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-id="element-20845">
              {ALL_ARTICLES.filter(a => a.id !== selectedArticle.id && a.category === selectedArticle.category).slice(0, 3).map(article => <Card key={article.id} hoverable padding="sm" className="flex items-center justify-between cursor-pointer" onClick={() => {
              setSelectedArticleId(article.id);
              window.scrollTo(0, 0);
            }} data-id="element-20846">
                    <span className="font-medium text-slate-700 line-clamp-1 text-sm sm:text-base" data-id="element-20847">
                      {article.title}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 ml-2" data-id="element-20848" />
                  </Card>)}
            </div>
          </div>

          {/* CTA Section */}
          <Card padding="lg" className="bg-slate-900 text-white border-none text-center mt-12 sm:mt-16" data-id="element-20849">
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" data-id="element-20850">
              {selectedArticle.isEncyclopedia ? t('enc.ctaEncTitle') : t('enc.ctaIntTitle')}
            </h3>
            <p className="text-slate-300 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base" data-id="element-20851">
              {selectedArticle.isEncyclopedia ? t('enc.ctaEncDesc') : t('enc.ctaIntDesc')}
            </p>
            <Button variant="primary" size="lg" onClick={() => onNavigate(selectedArticle.relatedService || 'service-natal')} data-id="element-20852">
              {selectedArticle.relatedServiceLabel || t('enc.defaultCta')}
            </Button>
          </Card>
        </div>
      </div>;
  }
  // ---------------------------------------------------------------------------
  // HUB VIEW
  // ---------------------------------------------------------------------------
  return <div className="w-full bg-slate-50 min-h-screen pb-16 sm:pb-24" data-id="element-20853">
      {/* Hero */}
      <div className="bg-white border-b border-slate-200 pt-10 pb-12 sm:pt-16 sm:pb-20" data-id="element-20854">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-20855">
          <Badge variant="pro" className="mb-4 sm:mb-6" data-id="element-20856">
            {t('enc.badge')}
          </Badge>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3 sm:mb-6" data-id="element-20857">
            {t('enc.title')}
          </h1>
          <p className="text-base sm:text-xl text-slate-600 mb-6 sm:mb-10 max-w-3xl mx-auto" data-id="element-20858">
            {t('enc.subtitle', {
            count: String(ALL_ARTICLES.length)
          })}
          </p>

          <div className="max-w-2xl mx-auto relative" data-id="element-20859">
            <div className="relative" data-id="element-20860">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-20861" />
              <input type="text" value={searchQuery} onChange={handleSearch} placeholder={t('enc.searchPlaceholder')} className="w-full pl-12 pr-4 py-3 sm:py-4 rounded-2xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-base sm:text-lg shadow-sm" data-id="element-20862" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" data-id="element-20863">
                  <X className="w-5 h-5" data-id="element-20864" />
                </button>}
            </div>
            <div className="mt-3 sm:mt-4 flex flex-wrap justify-center gap-1.5 sm:gap-2 text-sm" data-id="element-20865">
              <span className="text-slate-500" data-id="element-20866">{t('enc.popular')}</span>
              {[t('enc.popularSearch1'), t('enc.popularSearch2'), t('enc.popularSearch3')].map(article => <button key={article} onClick={() => setSearchQuery(article)} className="text-primary hover:underline" data-id="element-20867">
                  {article}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-16" data-id="element-20868">
        {/* Results View (when filtering) */}
        {isFiltering ? <div data-id="element-20869">
            <div className="flex items-center justify-between mb-6 sm:mb-8" data-id="element-20870">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-20871">
                {t('enc.results', {
              count: String(filteredArticles.length)
            })}
              </h2>
              <button onClick={clearFilters} className="text-sm font-medium text-primary hover:text-primary/80" data-id="element-20872">
                {t('enc.resetFilters')}
              </button>
            </div>

            {filteredArticles.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-20873">
                {filteredArticles.map(article => <Card key={article.id} hoverable padding="md" onClick={() => setSelectedArticleId(article.id)} className="flex flex-col h-full" data-id="element-20874">
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2" data-id="element-20875">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-3 sm:mb-4 flex-grow" data-id="element-20876">
                      {article.text}
                    </p>
                    <div className="flex items-center text-primary text-sm font-medium mt-auto pt-3 sm:pt-4 border-t border-slate-100" data-id="element-20877">
                      {t('enc.readMore')}{' '}
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-20878" />
                    </div>
                  </Card>)}
              </div> : <div className="text-center py-16 sm:py-20 bg-white rounded-2xl border border-slate-200" data-id="element-20879">
                <Search className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-4" data-id="element-20880" />
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2" data-id="element-20881">
                  {t('enc.notFound')}
                </h3>
                <p className="text-slate-500 text-sm sm:text-base" data-id="element-20882">
                  {t('enc.notFoundHint')}
                </p>
                <Button variant="secondary" className="mt-6" onClick={clearFilters} data-id="element-20883">
                  {t('enc.showCategories')}
                </Button>
              </div>}
          </div> : <>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8" data-id="element-20884">
              {t('enc.basicsTitle')}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-10" data-id="element-20885">
              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('basics')} data-id="element-20886">
                <div className="flex items-center gap-3 mb-2" data-id="element-20887">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-20888">
                    <BookOpen className="w-5 h-5 text-indigo-500" data-id="element-20889" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20890">
                    {t('enc.basicsCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20891">
                  {t('enc.basicsDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20892">
                  <span className="text-xs text-slate-500" data-id="element-20893">
                    {ALL_ARTICLES.filter(a => a.category === 'basics').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20894" />
                </div>
              </Card>

              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('advanced')} data-id="element-20895">
                <div className="flex items-center gap-3 mb-2" data-id="element-20896">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-20897">
                    <Star className="w-5 h-5 text-purple-500" data-id="element-20898" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20899">
                    {t('enc.advancedCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20900">
                  {t('enc.advancedDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20901">
                  <span className="text-xs text-slate-500" data-id="element-20902">
                    {ALL_ARTICLES.filter(a => a.category === 'advanced').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20903" />
                </div>
              </Card>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8" data-id="element-20904">
              {t('enc.interpretationsTitle')}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10" data-id="element-20905">
              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('planets-signs')} data-id="element-20906">
                <div className="flex items-center gap-3 mb-2" data-id="element-20907">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-20908">
                    <Sun className="w-5 h-5 text-amber-500" data-id="element-20909" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20910">
                    {t('enc.planetsSignsCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20911">
                  {t('enc.planetsSignsDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20912">
                  <span className="text-xs text-slate-500" data-id="element-20913">
                    {ALL_ARTICLES.filter(a => a.category === 'planets-signs').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20914" />
                </div>
              </Card>

              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('ascendant')} data-id="element-20915">
                <div className="flex items-center gap-3 mb-2" data-id="element-20916">
                  <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-20917">
                    <Star className="w-5 h-5 text-indigo-500" data-id="element-20918" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20919">
                    {t('enc.ascendantCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20920">
                  {t('enc.ascendantDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20921">
                  <span className="text-xs text-slate-500" data-id="element-20922">
                    {ALL_ARTICLES.filter(a => a.category === 'ascendant').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20923" />
                </div>
              </Card>

              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('planets-houses')} data-id="element-20924">
                <div className="flex items-center gap-3 mb-2" data-id="element-20925">
                  <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0" data-id="element-20926">
                    <Moon className="w-5 h-5 text-rose-500" data-id="element-20927" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20928">
                    {t('enc.housesCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20929">
                  {t('enc.housesDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20930">
                  <span className="text-xs text-slate-500" data-id="element-20931">
                    {ALL_ARTICLES.filter(a => a.category === 'planets-houses').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20932" />
                </div>
              </Card>

              <Card hoverable padding="md" className="flex flex-col cursor-pointer border-2 border-transparent hover:border-primary/20" onClick={() => handleCategoryClick('aspects')} data-id="element-20933">
                <div className="flex items-center gap-3 mb-2" data-id="element-20934">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-20935">
                    <Heart className="w-5 h-5 text-emerald-500" data-id="element-20936" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900" data-id="element-20937">
                    {t('enc.aspectsCard')}
                  </h3>
                </div>
                <p className="text-slate-600 text-xs sm:text-sm mb-3 flex-grow line-clamp-2" data-id="element-20938">
                  {t('enc.aspectsDesc')}
                </p>
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-slate-100" data-id="element-20939">
                  <span className="text-xs text-slate-500" data-id="element-20940">
                    {ALL_ARTICLES.filter(a => a.category === 'aspects').length}{' '}
                    {t('enc.articles')}
                  </span>
                  <ArrowRight className="w-4 h-4 text-primary" data-id="element-20941" />
                </div>
              </Card>
            </div>
          </>}

        {/* Alphabetical Index */}
        <div className="mt-12 sm:mt-20" data-id="element-20942">
          <Card padding="lg" data-id="element-20943">
            <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-8" data-id="element-20944">
              {t('enc.alphabetTitle')}
            </h2>
            <div className="flex flex-wrap gap-1 sm:gap-2" data-id="element-20945">
              {alphabet.map(letter => {
              const isActive = selectedLetter === letter;
              const hasArticles = ALL_ARTICLES.some(a => a.title.toUpperCase().startsWith(letter));
              return <button key={letter} onClick={() => handleLetterClick(letter)} disabled={!hasArticles} className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-xs sm:text-sm font-medium transition-colors ${isActive ? 'bg-primary text-white shadow-md' : hasArticles ? 'text-slate-700 bg-slate-100 hover:bg-primary/10 hover:text-primary' : 'text-slate-300 bg-slate-50 cursor-not-allowed'}`} data-id="element-20946">
                    {letter}
                  </button>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}